import { track } from "@vercel/analytics";
import { usePathname } from "next/navigation";
import { useCallback } from "react";

import { logger } from "@/lib/logger";

/**
 * Hook for tracking user interactions and analytics events
 */
export function useAnalytics() {
  const pathname = usePathname();

  /**
   * Track user interaction events
   */
  const trackInteraction = useCallback(
    (
      type:
        | "click"
        | "navigation"
        | "form_submit"
        | "search"
        | "filter"
        | "cart_action",
      element?: string,
      metadata?: Record<string, unknown>
    ) => {
      // Log to our internal logging system
      logger.logUserInteraction({
        type,
        element,
        page: pathname,
        metadata,
      });

      // Track with Vercel Analytics
      const eventData: Record<string, string | number | boolean> = {
        page: pathname,
        ...(element && { element }),
        ...(metadata &&
          Object.fromEntries(
            Object.entries(metadata).filter(
              ([, value]) => value !== undefined && value !== null
            )
          )),
      };

      track(`${type}${element ? `_${element}` : ""}`, eventData);
    },
    [pathname]
  );

  /**
   * Track API call performance from client-side
   */
  const trackAPICall = useCallback(
    (
      endpoint: string,
      method: string,
      duration: number,
      status: number,
      statusText?: string,
      errorCode?: string
    ) => {
      logger.logAPICall({
        endpoint,
        method,
        duration,
        status,
        statusText,
        errorCode,
      });
    },
    []
  );

  /**
   * Track search events with enhanced Vercel Analytics integration
   */
  const trackSearch = useCallback(
    (
      query: string,
      resultsCount?: number,
      filters?: Record<string, unknown>
    ) => {
      // Track with our internal system
      trackInteraction("search", "search-input", {
        query,
        resultsCount,
        filters,
      });

      // Track specific search event with Vercel Analytics
      track("Search", {
        query: query.toLowerCase(),
        results_count: resultsCount || 0,
        has_filters: filters ? Object.keys(filters).length > 0 : false,
        page: pathname,
      });
    },
    [trackInteraction, pathname]
  );

  /**
   * Track filter usage
   */
  const trackFilter = useCallback(
    (
      filterType: string,
      filterValue: string | string[],
      action: "add" | "remove" | "clear"
    ) => {
      trackInteraction("filter", `filter-${filterType}`, {
        filterType,
        filterValue,
        action,
      });
    },
    [trackInteraction]
  );

  /**
   * Track cart actions with enhanced e-commerce tracking
   */
  const trackCartAction = useCallback(
    (
      action: "add" | "remove" | "update" | "clear",
      productId?: string,
      quantity?: number,
      productName?: string,
      price?: number
    ) => {
      // Track with our internal system
      trackInteraction("cart_action", `cart-${action}`, {
        action,
        productId,
        quantity,
        productName,
        price,
      });

      // Track specific e-commerce events with Vercel Analytics
      let eventName: string;
      if (action === "add") {
        eventName = "Add to Cart";
      } else if (action === "remove") {
        eventName = "Remove from Cart";
      } else if (action === "update") {
        eventName = "Update Cart";
      } else {
        eventName = "Clear Cart";
      }

      track(eventName, {
        product_id: productId || "unknown",
        quantity: quantity || 1,
        ...(productName && { product_name: productName }),
        ...(price && { price }),
        page: pathname,
      });
    },
    [trackInteraction, pathname]
  );

  /**
   * Track form submissions
   */
  const trackFormSubmit = useCallback(
    (formType: string, success: boolean, errorMessage?: string) => {
      trackInteraction("form_submit", `form-${formType}`, {
        formType,
        success,
        errorMessage,
      });
    },
    [trackInteraction]
  );

  /**
   * Track navigation events
   */
  const trackNavigation = useCallback(
    (destination: string, source?: string) => {
      trackInteraction("navigation", destination, {
        destination,
        source,
        previousPage: pathname,
      });
    },
    [trackInteraction, pathname]
  );

  /**
   * Track product view events
   */
  const trackProductView = useCallback(
    (
      productId: string,
      productName: string,
      category?: string,
      price?: number
    ) => {
      trackInteraction("click", "product-view", {
        productId,
        productName,
        category,
        price,
      });

      track("Product View", {
        product_id: productId,
        product_name: productName,
        ...(category && { category }),
        ...(price && { price }),
        page: pathname,
      });
    },
    [trackInteraction, pathname]
  );

  /**
   * Track checkout events
   */
  const trackCheckout = useCallback(
    (step: "start" | "complete", orderValue?: number, itemCount?: number) => {
      const eventName =
        step === "start" ? "Checkout Started" : "Purchase Complete";

      trackInteraction("form_submit", `checkout-${step}`, {
        step,
        orderValue,
        itemCount,
      });

      track(eventName, {
        step,
        ...(orderValue && { order_value: orderValue }),
        ...(itemCount && { item_count: itemCount }),
        page: pathname,
      });
    },
    [trackInteraction, pathname]
  );

  /**
   * Track supplement-specific events for WillPower Store
   */
  const trackSupplementEvent = useCallback(
    (
      event:
        | "filter_by_brand"
        | "filter_by_category"
        | "view_nutrition_facts"
        | "compare_products",
      data?: Record<string, unknown>
    ) => {
      trackInteraction("click", `supplement-${event}`, data);

      track(`Supplement ${event.replace(/_/g, " ")}`, {
        ...data,
        page: pathname,
      });
    },
    [trackInteraction, pathname]
  );

  /**
   * Track social share events
   */
  const trackSocialShare = useCallback(
    (
      platform: "native" | "facebook" | "twitter" | "whatsapp",
      productUrl: string,
      productTitle?: string
    ) => {
      trackInteraction("click", `social-share-${platform}`, {
        productUrl,
        productTitle,
      });

      track("Social Share", {
        platform,
        product_url: productUrl,
        product_title: productTitle || "unknown",
        page: pathname,
      });
    },
    [trackInteraction, pathname]
  );

  return {
    trackInteraction,
    trackAPICall,
    trackSearch,
    trackFilter,
    trackCartAction,
    trackFormSubmit,
    trackNavigation,
    trackProductView,
    trackCheckout,
    trackSupplementEvent,
    trackSocialShare,
  };
}
